import type { PlayerSummary } from '@/entities/session/types';
import { clientEnv } from '@/shared/config/env';
import { requestJson } from '@/shared/request/http-client';

export interface ProfileUpdateRequest {
  avatarId?: string;
  birthday?: string;
  displayName: string;
  email?: string;
  loginName?: string;
  password?: string;
  phone?: string;
  siteId: string;
}

export const getProfile = (authToken: string) =>
  requestJson<PlayerSummary>(`/v1/app/profile?siteId=${encodeURIComponent(clientEnv.siteId)}`, {
    authToken,
    method: 'GET',
  });

export const updateProfile = (authToken: string, payload: Omit<ProfileUpdateRequest, 'siteId'>) =>
  requestJson<PlayerSummary, ProfileUpdateRequest>('/v1/app/profile', {
    authToken,
    body: { ...payload, siteId: clientEnv.siteId },
    method: 'PATCH',
  });

export interface PayPasswordStatus {
  hasPayPassword: boolean;
}

export const getPayPasswordStatus = (authToken: string) =>
  requestJson<PayPasswordStatus>(
    `/v1/app/profile/pay-password?siteId=${encodeURIComponent(clientEnv.siteId)}`,
    {
      authToken,
      method: 'GET',
    },
  );

export interface LoginDeviceItem {
  clientIp: string;
  current: boolean;
  deviceLabel: string;
  loginAt: string;
  loginRecordId: number;
}

export interface LoginDeviceList {
  items: LoginDeviceItem[];
}

export const listLoginDevices = (authToken: string) =>
  requestJson<LoginDeviceList>(
    `/v1/app/profile/login-devices?siteId=${encodeURIComponent(clientEnv.siteId)}`,
    {
      authToken,
      method: 'GET',
    },
  );

export interface SecurityStatus {
  emailMasked: string | null;
  hasEmail: boolean;
  hasPhone?: boolean;
  phoneMasked?: string | null;
}

export const getSecurityStatus = (authToken: string) =>
  requestJson<SecurityStatus>(
    `/v1/app/profile/security?siteId=${encodeURIComponent(clientEnv.siteId)}`,
    {
      authToken,
      method: 'GET',
    },
  );

export const updateLoginPassword = (
  authToken: string,
  payload: { oldPassword: string; password: string },
) =>
  requestJson<boolean, { oldPassword: string; password: string; siteId: string }>(
    '/v1/app/profile/login-password',
    {
      authToken,
      body: {
        oldPassword: payload.oldPassword,
        password: payload.password,
        siteId: clientEnv.siteId,
      },
      method: 'PUT',
    },
  );

export const sendPhoneCode = (
  authToken: string,
  payload: { loginPassword?: string; phone: string },
) =>
  requestJson<boolean, { loginPassword?: string; phone: string; siteId: string }>(
    '/v1/app/profile/phone/code',
    {
      authToken,
      body: {
        loginPassword: payload.loginPassword,
        phone: payload.phone,
        siteId: clientEnv.siteId,
      },
      method: 'POST',
    },
  );

export const completeGuestBind = (
  authToken: string,
  payload: { code: string; password: string; phone: string },
) =>
  requestJson<SecurityStatus, { code: string; password: string; phone: string; siteId: string }>(
    '/v1/app/profile/guest-bind',
    {
      authToken,
      body: {
        code: payload.code,
        password: payload.password,
        phone: payload.phone,
        siteId: clientEnv.siteId,
      },
      method: 'PUT',
    },
  );

export const bindPhone = (
  authToken: string,
  payload: { code: string; loginPassword?: string; phone: string },
) =>
  requestJson<SecurityStatus, { code: string; loginPassword?: string; phone: string; siteId: string }>(
    '/v1/app/profile/phone',
    {
      authToken,
      body: {
        code: payload.code,
        loginPassword: payload.loginPassword,
        phone: payload.phone,
        siteId: clientEnv.siteId,
      },
      method: 'PUT',
    },
  );

export const sendEmailCode = (
  authToken: string,
  payload: { email: string },
) =>
  requestJson<boolean, { email: string; siteId: string }>(
    '/v1/app/profile/email/code',
    {
      authToken,
      body: {
        email: payload.email,
        siteId: clientEnv.siteId,
      },
      method: 'POST',
    },
  );

export const bindEmail = (
  authToken: string,
  payload: { code: string; email: string },
) =>
  requestJson<SecurityStatus, { code: string; email: string; siteId: string }>(
    '/v1/app/profile/email',
    {
      authToken,
      body: {
        code: payload.code,
        email: payload.email,
        siteId: clientEnv.siteId,
      },
      method: 'PUT',
    },
  );

export const updatePayPassword = (
  authToken: string,
  payload: { oldPayPassword?: string; payPassword: string },
) =>
  requestJson<PayPasswordStatus, { oldPayPassword?: string; payPassword: string; siteId: string }>(
    '/v1/app/profile/pay-password',
    {
      authToken,
      body: {
        oldPayPassword: payload.oldPayPassword,
        payPassword: payload.payPassword,
        siteId: clientEnv.siteId,
      },
      method: 'PUT',
    },
  );
